import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useLayoutEffect, useCallback } from "react";
import {
  useCopyOverride,
  usePromoActive,
  useColorOverride,
  promoPreview,
  COPY_DEFAULTS,
} from "@/lib/copyOverrides";
import { sendPromoEvent, sendPromoViewOnce } from "@/lib/promoEvents";


const PromoBanner = () => {
  const [isHidden, setIsHidden] = useState(false);
  const [barEl, setBarEl] = useState<HTMLElement | null>(null);
  const location = useLocation();

  // Measure in the ref callback so the header is offset during the banner's
  // first commit, before the browser has a chance to paint both at top: 0.
  const attachBar = useCallback((node: HTMLElement | null) => {
    setBarEl(node);
    if (node) {
      document.documentElement.style.setProperty(
        "--promo-banner-height",
        `${Math.ceil(node.getBoundingClientRect().height)}px`,
      );
    }
  }, []);

  const desktopCopy = useCopyOverride("banner.desktop", COPY_DEFAULTS["banner.desktop"]);
  const mobileCopy = useCopyOverride("banner.mobile", COPY_DEFAULTS["banner.mobile"]);
  const href = useCopyOverride("banner.href", COPY_DEFAULTS["banner.href"]);
  const bannerActive = usePromoActive("banner");
  const bg = useColorOverride("banner.bg");
  const fg = useColorOverride("banner.fg");
  const preview = promoPreview();

  // Hide on booking, admin, and pay routes so it never covers the funnel
  const p = location.pathname;
  const hideBanner =
    p === "/book" ||
    p === "/book-now" ||
    p.startsWith("/book-now/") ||
    p.startsWith("/book5") ||
    p.startsWith("/admin") ||
    p.startsWith("/pay/") ||
    p.startsWith("/map5") ||
    p === "/waivers" ||
    p === "/sharedwaivers";

  const visible = !hideBanner && (bannerActive || preview === "banner");

  useEffect(() => {
    if (visible) sendPromoViewOnce("banner", p);
  }, [visible, p]);

  // Publish the banner's live height so the header can sit below it (never covered)
  useLayoutEffect(() => {
    const root = document.documentElement;
    const setH = (h: number) =>
      root.style.setProperty("--promo-banner-height", `${Math.round(h)}px`);

    if (!visible || !barEl) {
      setH(0);
      return;
    }
    setH(barEl.getBoundingClientRect().height);
    const ro = new ResizeObserver(() => {
      // contentRect excludes the banner's vertical padding, which previously
      // left the header tucked underneath it. Measure the full rendered box.
      setH(barEl.getBoundingClientRect().height);
    });
    ro.observe(barEl);
    return () => {
      ro.disconnect();
      setH(0);
    };
  }, [visible, barEl]);

  if (!visible) return null;


  const isExternal = /^https?:\/\//i.test(href);
  const className = `fixed top-0 left-0 right-0 z-[60] block w-full text-center transition-all duration-300 ${
    bg ? "" : "bg-white hover:bg-gray-50"
  } ${
    isHidden
      ? "py-0 opacity-0 pointer-events-none -translate-y-full"
      : "py-3 sm:py-3 opacity-100 translate-y-0"
  }`;
  const barStyle: React.CSSProperties = {
    ...(bg ? { backgroundColor: bg } : {}),
    ...(fg ? { color: fg } : {}),
  };
  const textStyle: React.CSSProperties = fg ? { color: fg } : {};
  const onBannerClick = () => sendPromoEvent("banner", "click");

  const inner = (
    <>
      <span
        style={textStyle}
        className={`hidden sm:flex items-center justify-center gap-2 ${fg ? "" : "text-[#005283]"} font-semibold px-2 leading-tight text-base whitespace-pre-wrap`}
      >
        {desktopCopy}
      </span>
      <span
        style={textStyle}
        className={`flex sm:hidden items-center justify-center gap-1 ${fg ? "" : "text-[#005283]"} font-semibold px-2 leading-tight text-[11px] whitespace-pre-wrap`}
      >
        {mobileCopy}
      </span>

    </>
  );

  const isControl = p === "/control";

  return (
    <>
      {isExternal ? (
        <a
          ref={attachBar}
          data-promo-banner
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          style={barStyle}
          onClick={onBannerClick}
        >
          {inner}
        </a>
      ) : (
        <Link ref={attachBar} data-promo-banner to={href} className={className} style={barStyle} onClick={onBannerClick}>
          {inner}
        </Link>

      )}
      {isControl && !isHidden && (
        <div className="fixed top-[44px] sm:top-[48px] left-0 right-0 z-[60] flex justify-center pointer-events-none">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById("control-banner-editor")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="pointer-events-auto mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1 shadow-md"
          >
            ✎ Edit banner
          </button>
        </div>
      )}
    </>
  );
};

export default PromoBanner;